import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Col, Form, Row } from "antd";
import { H3 } from "../../../components/Typography";
import { FormInstance } from "antd/lib/form/Form";
import { LMDropdown, LMNewInput } from "../../../components";
import { IUserFormValues } from "../types";
import { useAppNotifications, getErrorMessage } from "../../../components/LMNotifications";
import { createUser } from "../api";
import { useUser } from "../hooks";
import styles from "./UserFormModal.module.css";

interface Props {
  onClose: () => void;
  onRefresh: () => void;
  onSetLoading: (loading: boolean) => void;
  form: FormInstance<IUserFormValues>;
}

export const CreateUserForm: FC<Props> = ({
  onClose,
  onRefresh,
  form,
  onSetLoading,
}) => {
  const { t } = useTranslation("users");
  const { availableUserTypesList } = useUser();
  const { contextHolder, success, error } = useAppNotifications();

  const onFinish = async (values: IUserFormValues) => {
    onSetLoading(true);
    try {
      await createUser(values);
      await setTimeout(() => {
        onRefresh();
        onSetLoading(false);
        form.resetFields();
        success(t("createSuccessMessage"));
        onClose();
      }, 3000);
    } catch (e) {
      onSetLoading(false);
      error(t("createErrorMessage"), getErrorMessage(e));
    }
  };

  return (
    <>
      {contextHolder}
      <div className={styles.container}>
        <H3 className={styles.title}>{t("addNew")}</H3>
        <Form
          layout={"vertical"}
          form={form}
          className={styles.form}
          onFinish={onFinish}
          autoComplete="off"
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                name="name"
                label={t("name")}
                rules={[{ required: true, message: t("requiredMessage") }]}
              >
                <LMNewInput placeholder={t("namePlaceholder")} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                name="email"
                label={t("email")}
                rules={[
                  { required: true, message: t("requiredMessage") },
                  {
                    required: true,
                    type: "email",
                    message: t("emailValidationMessage"),
                  },
                ]}
              >
                <LMNewInput placeholder={t("emailPlaceholder")} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                label={t("type")}
                name="userType"
                rules={[{ required: true, message: t("requiredMessage") }]}
              >
                <LMDropdown
                  items={availableUserTypesList.map((type) => ({
                    name: t(type),
                    value: type,
                  }))}
                  placeholder={t("pleaseSelect")}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </>
  );
};
